import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { getAccessTokenCookie } from "../../utils/cookies";
import { getEnv } from "../../utils/env";

/**
 * Admin-only report audit log (MDRS-271, widened by MDRS-455 to the full
 * event set — including doctor/admin downloads and previews — with real
 * pagination and an action filter).
 *
 * Hand-rolled `fetch` (like the report-finalisation hooks) so no
 * generated-client regen is needed. The endpoint is `require_admin`; the page
 * is also gated by `<AdminRoute>`.
 */

export type AuditLogRow = {
  id: string;
  action: string;
  created_at: string;
  actor_email: string | null;
  actor_name: string | null;
  actor_user_type: string | null;
  case_number: string | null;
  response_id: string | null;
  description: string | null;
  reason: string | null;
  prior_finalised_at: string | null;
  data: Record<string, unknown> | null;
};

export type AuditLogPage = {
  items: AuditLogRow[];
  total: number;
};

const auditUrl = () => {
  const baseUrl = getEnv("VITE_BACKEND_URL") || "http://localhost:8000";
  return `${baseUrl.replace(/\/$/, "")}/api/v1/admin/audit/events`;
};

export const useAuditLog = (params: {
  search?: string;
  userType?: string;
  action?: string;
  page?: number;
  limit?: number;
}) => {
  const page = params.page ?? 1;
  const limit = params.limit ?? 50;

  return useQuery({
    queryKey: [
      "admin",
      "audit-log",
      params.search ?? null,
      params.userType ?? null,
      params.action ?? null,
      page,
      limit,
    ],
    queryFn: async (): Promise<AuditLogPage> => {
      const token = getAccessTokenCookie();
      const qs = new URLSearchParams();
      if (params.search) qs.set("search", params.search);
      if (params.userType) qs.set("user_type", params.userType);
      if (params.action) qs.set("action", params.action);
      qs.set("limit", String(limit));
      qs.set("offset", String((page - 1) * limit));
      const resp = await fetch(`${auditUrl()}?${qs.toString()}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!resp.ok) {
        const detail = await resp.text().catch(() => "");
        throw new Error(
          `Failed to load audit log (${resp.status}): ${detail || resp.statusText}`
        );
      }
      return resp.json();
    },
    // Keep showing the current page while a filter change or page navigation's
    // request is in flight, instead of flashing to the loading state.
    placeholderData: keepPreviousData,
  });
};
